import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import supabase from "../supabase";
import HomeNav from "./HomeNav";
import { EyeIcon, EyeOffIcon } from "./icons/EyeIcon";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isResetMode, setIsResetMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const completeLogin = useCallback(
    async (userId) => {
      const { data: profileData, error: profileError } = await supabase
        .from("Users")
        .select("*")
        .eq("id", userId)
        .single();

      if (profileError || !profileData) {
        await supabase.auth.signOut();
        throw new Error(
          "No account found for this email. Ask your admin to invite you, or sign up your company."
        );
      }

      const companyId = profileData.company_id;
      const userRole = profileData.role;

      // If a route guard sent the user here, return them to the page they
      // were actually trying to reach instead of the default destination —
      // but never honor that for the super-admin dashboard unless the user
      // actually is a superAdmin. Otherwise a Writer who merely typed that
      // URL while logged out (which stores it as "from") would get routed
      // straight there on their next login, only to be bounced back out by
      // the dashboard's own role check a moment later.
      const from = location.state?.from;
      const fromIsSuperAdminOnly = from?.startsWith("/super-admin-dashboard");
      if (from && (userRole === "superAdmin" || !fromIsSuperAdminOnly)) {
        navigate(from, { state: { profile: profileData } });
        return;
      }

      if (userRole === "superAdmin") {
        navigate(`/super-admin-dashboard`, {
          state: { profile: profileData },
        });
      } else {
        navigate(`/organization-portal/${companyId}`, {
          state: { profile: profileData },
        });
      }
    },
    [navigate, location]
  );

  // Catches the user after Google's full-page redirect back to /login,
  // where there's no synchronous auth result to act on like the password
  // flow. Gated on a flag we set ourselves right before leaving for Google
  // (cleared the moment we come back), rather than on Supabase session/auth
  // events — those fire the same way for a stale session already sitting in
  // storage from a previous visit as they do for a fresh sign-in in this
  // library version, which was auto-redirecting people away from /login
  // before they could log in as someone else. This flag is the one signal
  // we fully control, so a plain visit to /login never auto-redirects.
  useEffect(() => {
    if (sessionStorage.getItem("oauthRedirectPending") !== "1") return;
    sessionStorage.removeItem("oauthRedirectPending");

    (async () => {
      const { data } = await supabase.auth.getSession();
      const userId = data?.session?.user?.id;
      if (!userId) return;

      try {
        await completeLogin(userId);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [completeLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authError) throw authError;

      await completeLogin(authData.user.id);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    sessionStorage.setItem("oauthRedirectPending", "1");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/login` },
    });
    if (error) {
      sessionStorage.removeItem("oauthRedirectPending");
      setError(error.message);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (!email) {
      setError("Please enter your email address.");
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      setMessage("Password reset email sent! Please check your inbox.");
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh" }}>
      <HomeNav />
      <div style={styles.pageContainer}>
        <div style={styles.card}>
          <img
            style={styles.logo}
            src="/Long_Black_Transparent.png"
            alt="Logo"
          />
          <h1 style={styles.title}>
            {isResetMode ? "Reset Password" : "Welcome Back"}
          </h1>

          {error && <div style={styles.errorAlert}>{error}</div>}
          {message && <div style={styles.successAlert}>{message}</div>}

          {isResetMode ? (
            <form onSubmit={handlePasswordReset} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
              <p
                style={styles.linkText}
                onClick={() => {
                  setIsResetMode(false);
                  setError("");
                  setMessage("");
                }}
              >
                Back to Login
              </p>
            </form>
          ) : (
            <form onSubmit={handleLogin} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordWrapper}>
                  <input
                    style={{ ...styles.input, ...styles.passwordInput }}
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.eyeButton}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
              <p
                style={styles.linkText}
                onClick={() => {
                  setIsResetMode(true);
                  setError("");
                  setMessage("");
                }}
              >
                Forgot Password?
              </p>
            </form>
          )}

          {!isResetMode && (
            <>
              <div style={styles.dividerRow}>
                <div style={styles.dividerLine} />
                <span style={styles.dividerText}>or</span>
                <div style={styles.dividerLine} />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                style={styles.googleButton}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62Z"
                  />
                  <path
                    fill="#34A853"
                    d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18Z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.33Z"
                  />
                  <path
                    fill="#EA4335"
                    d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58Z"
                  />
                </svg>
                Continue with Google
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #e5e7eb",
    padding: "40px",
    width: "100%",
    maxWidth: "420px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    width: "180px",
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#111827",
    margin: "0 0 24px 0",
    textAlign: "center",
  },
  form: {
    width: "100%",
  },
  inputGroup: {
    marginBottom: "16px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "700",
    color: "#4b5563",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    color: "#111827",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s",
  },
  passwordWrapper: {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    display: "block",
    paddingRight: "40px",
  },
  eyeButton: {
    position: "absolute",
    right: "10px",
    top: 0,
    bottom: 0,
    margin: "auto 0",
    height: "18px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    width: "100%",
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 14px",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
    marginTop: "12px",
    transition: "background 0.2s",
  },
  dividerRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    margin: "20px 0",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "#e5e7eb",
  },
  dividerText: {
    fontSize: "12px",
    color: "#9ca3af",
    fontWeight: "600",
  },
  googleButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    background: "#fff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "11px 14px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
  linkText: {
    textAlign: "center",
    marginTop: "20px",
    cursor: "pointer",
    color: "#00876e",
    fontSize: "14px",
    fontWeight: "600",
    margin: "20px 0 0 0",
  },
  errorAlert: {
    width: "100%",
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #fecaca",
    fontSize: "13px",
    marginBottom: "20px",
    boxSizing: "border-box",
  },
  successAlert: {
    width: "100%",
    backgroundColor: "#ecfdf5",
    color: "#059669",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #a7f3d0",
    fontSize: "13px",
    marginBottom: "20px",
    boxSizing: "border-box",
  },
};

export default Login;
